import React from "react";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MenuIcon from "@mui/icons-material/Menu";

import {
  Checkbox,
  ClickAwayListener,
  Grow,
  IconButton,
  MenuList,
  Paper,
  Popper,
  Tooltip,
  TooltipProps,
  Typography,
  Box,
  IconButtonProps,
} from "@mui/material";
import { SystemStyleObject } from "@mui/system";

export interface SimpleMenuItem {
  name: string;
  type?: "checkbox" | "action";
  disabled?: boolean;
  icon?: React.ReactNode;
  checked?: boolean;
  action: () => void;
  dataPendo?: string;
}
export interface SimpleMenuProps {
  id?: string;
  className?: string;
  content?: React.ReactNode;
  meatball?: boolean;
  hamburger?: boolean;
  customIcon?: React.ReactNode;
  iconButtonProps?: IconButtonProps;
  items: SimpleMenuItem[];
  sx?: SystemStyleObject;
  dataPendo?: string;
  disabled?: boolean;
  iconTooltip?: string;
  iconTooltipProps?: Omit<TooltipProps, "title" | "children">;
}
const SimpleMenu = (props: SimpleMenuProps) => {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const meatballs = Boolean(props.meatball);
  const hamburger = Boolean(props.hamburger);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current && !open) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleAction = (_event: React.MouseEvent<EventTarget>, fn: () => void) => {
    fn();
    handleClose();
  };

  return (
    <Box id={props.id} className={props.className} sx={props.sx}>
      <Tooltip title={props.iconTooltip} {...props.iconTooltipProps}>
        <span>
          {props.customIcon ? (
            <IconButton
              size="small"
              onClick={handleToggle}
              ref={anchorRef}
              data-pendo={props.dataPendo}
              disabled={props.disabled}
              {...props.iconButtonProps}>
              {props.customIcon}
            </IconButton>
          ) : meatballs ? (
            <IconButton
              size="small"
              onClick={handleToggle}
              ref={anchorRef}
              data-pendo={props.dataPendo}
              disabled={props.disabled}>
              <MoreVertIcon fontSize="large" />
            </IconButton>
          ) : hamburger ? (
            <IconButton
              size="small"
              onClick={handleToggle}
              ref={anchorRef}
              data-pendo={props.dataPendo}
              disabled={props.disabled}>
              <MenuIcon fontSize="large" />
            </IconButton>
          ) : (
            <Button
              size="small"
              style={{ textTransform: "none" }}
              onClick={handleToggle}
              ref={anchorRef}
              data-pendo={props.dataPendo}
              disabled={props.disabled}>
              {props.content}
            </Button>
          )}
        </span>
      </Tooltip>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        transition
        placement="right"
        sx={{
          zIndex: 10000,
        }}>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem={open} id="menu-list-grow">
                  {props.items.map((i) => {
                    if (i.type === "checkbox") {
                      return (
                        <MenuItem
                          key={i.name}
                          disabled={i.disabled}
                          onClick={(evt) => handleAction(evt, i.action)}
                          data-pendo={i.dataPendo}>
                          <Checkbox checked={i.checked || false} />
                          <Typography
                            style={{
                              marginLeft: 8,
                            }}>
                            {i.name}
                          </Typography>
                        </MenuItem>
                      );
                    } else {
                      return (
                        <MenuItem
                          key={i.name}
                          disabled={i.disabled}
                          onClick={(evt) => handleAction(evt, i.action)}
                          data-pendo={i.dataPendo}>
                          {i.icon && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: 1,
                                width: "40px",
                              }}>
                              {i.icon}
                            </Box>
                          )}
                          <Typography
                            style={{
                              marginLeft: i.icon ? 8 : 0,
                            }}>
                            {i.name}
                          </Typography>
                        </MenuItem>
                      );
                    }
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};

export default SimpleMenu;
